import { differenceInSeconds } from "date-fns";
import { getAttributionContext } from "./attribution-context";
import { events } from "./constants";
import { event } from "./gtag";
import { isDev } from "./helpers";

interface Analytics {
  track: (action: string, data: any, options?: { context?: any }) => void;
  page: (category?: string | null, name?: string | null, properties?: any, options?: { context?: any }) => void;
  identify?: (userId: string, traits?: any, options?: any) => void;
}

declare global {
  interface Window {
    analytics?: Analytics;
  }
}

interface CapturedEvent {
  [key: string]: {
    value?: string;
    timestamp: Date;
  };
}

const capturedEvents: CapturedEvent = {};
const staticValues: {
  fbp: string | null;
  fbc: string | null;
} = {
  fbp: null,
  fbc: null,
};

interface EventData {
  [key: string]: any; // Replace 'any' with more specific types as needed
}

const updateCapturedEvents = (action: string, data: EventData = {}): void => {
  if (!action) return;
  capturedEvents[action] = {
    ...data,
    timestamp: new Date(),
  };
};

const eventIsDuplicated = (action: string, data: EventData): boolean => {
  if (action === "Match_Quiz_CTA_Clicked") return false;

  if (!data?.value) return false;
  if (typeof data?.value !== "string") return false;
  if (!capturedEvents[action]?.value) return false;
  if (capturedEvents[action].value !== data.value) return false;
  if (!capturedEvents[action].timestamp) return false;

  return differenceInSeconds(new Date(), capturedEvents[action].timestamp) < 10;
};

export interface CaptureEventParams {
  action: string;
  skipMapping?: boolean;
  context?: {
    ip?: string;
    [key: string]: any;
  };
  [key: string]: any; // Replace 'any' with more specific types as needed
}

/**
 * Fetches IPv6 address from the public API
 * Only runs in the browser environment
 */
const getIPv6Address = async (): Promise<string | null> => {
  // Only run in browser environment
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const response = await fetch("https://api64.ipify.org?format=json");
    if (response.ok) {
      const data = await response.json();
      return data.ip;
    }
    return null;
  } catch (error) {
    if (isDev) {
      console.error("Error fetching IPv6 address:", error);
    }
    return null;
  }
};

// Constants for localStorage
const IPV6_STORAGE_KEY = "ladder_ipv6_address";
const IPV6_TIMESTAMP_KEY = "ladder_ipv6_timestamp";
const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours

// Cache for IPv6 address to reduce API calls
let cachedIPv6: string | null = null;
let lastFetchTime = 0;

/**
 * Gets IPv6 address from cache or fetches a new one
 * Uses both memory cache and localStorage for persistence
 */
const getCachedIPv6 = async (): Promise<string | null> => {
  const now = Date.now();

  // Check memory cache first
  if (cachedIPv6 && now - lastFetchTime < CACHE_DURATION) {
    return cachedIPv6;
  }

  // Check localStorage if available. The truthy check `window.localStorage`
  // itself throws SecurityError on iOS Safari in Private Browsing mode —
  // it's not just getItem/setItem that throws, it's the property access.
  // The previous outer-check-then-inner-try pattern missed this case
  // (LADDER-WEBSITE-89N, ~180 events/11 users on iOS 16). Moving the
  // availability check inside the try handles both shapes.
  try {
    if (typeof window !== "undefined" && window.localStorage) {
      const storedIPv6 = localStorage.getItem(IPV6_STORAGE_KEY);
      const storedTimestamp = localStorage.getItem(IPV6_TIMESTAMP_KEY);

      if (storedIPv6 && storedTimestamp) {
        const timestamp = parseInt(storedTimestamp, 10);

        // If the stored value is still valid, use it
        if (now - timestamp < CACHE_DURATION) {
          cachedIPv6 = storedIPv6;
          lastFetchTime = timestamp;
          return storedIPv6;
        }
      }
    }
  } catch (e) {
    // Ignore localStorage errors - they can happen in private browsing mode
    if (isDev) {
      console.error("Error accessing localStorage:", e);
    }
  }

  // Fetch new IPv6 address if needed
  const ipv6 = await getIPv6Address();

  if (ipv6) {
    // Update memory cache
    cachedIPv6 = ipv6;
    lastFetchTime = now;

    // Update localStorage if available. Same iOS Safari Private Browsing
    // SecurityError shape as the read path above.
    try {
      if (typeof window !== "undefined" && window.localStorage) {
        localStorage.setItem(IPV6_STORAGE_KEY, ipv6);
        localStorage.setItem(IPV6_TIMESTAMP_KEY, now.toString());
      }
    } catch (e) {
      // Ignore localStorage errors
      if (isDev) {
        console.error("Error writing to localStorage:", e);
      }
    }
  }

  return ipv6;
};

/**
 * Captures an event for analytics tracking
 * This is implemented to work without modifying existing code that uses captureEvent
 * @param params Event parameters
 * @returns Promise that resolves when the event is tracked (for async usage)
 */
export const captureEvent = ({
  action,
  skipMapping,
  ...data
}: CaptureEventParams): Promise<void> => {
  if (eventIsDuplicated(action, data)) return Promise.resolve();

  updateCapturedEvents(action, data);

  const fbc = staticValues.fbc || getFBC();
  const fbp = staticValues.fbp || getFBPCookie();

  // Read before the IPv6 await so a route change can't swap in the next page.
  const attribution = getAttributionContext();

  // Create and execute the promise but don't wait for IPv6 lookup
  const trackingPromise = (async () => {
    // Try to get IPv6 address
    const ipv6 = await getCachedIPv6();

    // Create context with ip if available
    const context = ipv6 ? { ip: ipv6 } : undefined;

    if (isDev) {
      // Simplified logging with dedicated IPv6 info
      console.log("captureEvent", {
        action,
        data: { ...data, fbc, fbp },
        ipv6: ipv6 || "not available",
      });
    }

    event({ action, ...data });

    if (typeof window?.analytics?.track === "undefined") return;

    // Context must be in the third argument (options) for Segment to forward IP to destinations
    // See: https://segment.com/docs/connections/spec/track/
    window.analytics.track(
      !skipMapping ? events[action] || action : action,
      { ...data, fbc, fbp, context: context ? context : undefined },
      { context: { ...attribution, ...context } }
    );
  })();

  // Return the promise for async usage, though most existing code won't await it
  return trackingPromise;
};

function getFBPCookie() {
  const name = "_fbp=";
  const decodedCookie = decodeURIComponent(document.cookie);
  const ca = decodedCookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) == " ") {
      c = c.substring(1);
    }
    if (c.indexOf(name) == 0) {
      const fbp = c.substring(name.length, c.length);
      staticValues.fbp = fbp;
      return fbp;
    }
  }
  return "";
}

function getFBC() {
  // Function to get the value of a specific cookie by name
  function getCookieValue(name) {
    const nameEQ = name + "=";
    const ca = document.cookie.split(";");
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === " ") c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  // Function to get the value of a query parameter by name
  function getQueryParam(param) {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(param);
  }

  // Check if `fbclid` is present in the URL
  const fbclid = getQueryParam("fbclid");
  if (fbclid) {
    // If `fbclid` is present, store it in a cookie
    const fbc = "fb.1." + Date.now() + "." + fbclid;
    document.cookie = `fbc=${fbc}; path=/; domain=${window.location.hostname}`;
    staticValues.fbc = fbc;
    return fbc;
  } else {
    // If `fbclid` is not present, try to get `fbc` from the cookie
    return getCookieValue("fbc");
  }
}
